import React, { useCallback, useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  PencilSquare,
  PlusLg,
  Search,
  Trash3,
  BoxSeam,
  GeoAlt,
  Grid,
  List,
} from "react-bootstrap-icons";
import Drawer from "../../components/Common/Drawer";
import TooltipWrapper from "../../components/Common/TooltipWrapper";
import { useNavigate } from "react-router-dom";
import { decryptData } from "../../utils/crypto";
import Utils from "../../utils";
import ConfirmModal from "../../components/Common/ConfirmModal";
import { deleteOwner, getStoreList } from "../../services/storeServices";
import { useTranslation } from "react-i18next";
import DesktopTableSkeleton from "../../components/Common/DesktopTableSkeleton";
import FlexibleSkeleton from "../../components/Common/FlexibleSkeleton";
import { useSelector } from "react-redux";
import CONFIG from "../../config";
import { notify } from "../../components/Common/Alerts";

const Store = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  let auth = useSelector((x) => x.auth.value);

  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [params, setParams] = useState({
    limit: CONFIG.DATA_LIMIT,
    page: 1,
    search: "",
    is_reload: true,
  });
  const [storeList, setStoreList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecord, setTotalRecord] = useState(0);
  const [deleteShow, setDeleteShow] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [btnDeleteLoading, setBtnDeleteLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
  });
  const [skeletonLoading, setSkeletonLoading] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "list"

  const currentPage = params.page;
  const totalPages = Math.ceil(totalRecord / params.limit);
  const startIndex = (currentPage - 1) * params.limit;
  const endIndex = startIndex + params.limit;

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleParams = (e, type) => {
    let paramsObj = { ...params };

    if (type === "length") {
      paramsObj.limit = Number(e.target.value);
      paramsObj.page = 1;
    } else if (type === "pagination") {
      paramsObj.page = e;
    }

    setParams(paramsObj);
  };

  const handleFilterChange = (value, key) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value?.target ? value.target.value : value?.value || "",
    }));
  };

  const handleSearch = () => {
    setParams((prev) => ({
      ...prev,
      ...filters,
      page: 1,
      is_reload: true,
    }));
    setShowOffcanvas(false);
  };

  const handleReset = () => {
    setFilters({
      search: "",
    });
    setParams({
      limit: 10,
      page: 1,
      search: "",
      is_reload: true,
    });
    setHasMore(true);
    setLoadingPage(false);
    setShowOffcanvas(false);
  };

  const handleDeleteClose = () => {
    setDeleteShow(false);
  };
  const handleDeleteShow = (e, item) => {
    setDeleteId(item);
    setDeleteShow(true);
  };

  const handleDeleteStore = (e) => {
    e.preventDefault();
    setBtnDeleteLoading(true);

    let data = {
      uuid: deleteId.uuid,
    };

    deleteOwner(data)
      .then(async (response) => {
        let decrypt = decryptData(response.data.data);
        notify.success(decrypt.message);

        setStoreList((prevList = []) =>
          prevList.filter((role) => role.uuid !== deleteId.uuid),
        );
        setDeleteShow(false);
        setBtnDeleteLoading(false);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
        setBtnDeleteLoading(false);
      });
  };

  const fetchStoreList = useCallback(() => {
    if (params.page === 1) {
      setLoading(true); // full page loader
    } else {
      setLoadingPage(true); // pagination loader
    }

    getStoreList(params)
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        const newData = decrypted.data || [];

        setStoreList((prev) =>
          params.is_reload ? newData : [...prev, ...newData],
        );

        setTotalRecord(decrypted.total || 0);

        if (newData.length < params.limit) setHasMore(false);
      })
      .finally(() => {
        if (!isMobile) setLoading(false);
        setLoadingPage(false);
        setSkeletonLoading(false);
      });
  }, [params]);

  useEffect(() => {
    fetchStoreList();
  }, [params]);

  useEffect(() => {
    if (!isMobile) setLoading(false);
    setSkeletonLoading(true);

    // Reset state
    // setUnitsList([]);
    setHasMore(true);

    setParams((prev) => ({
      ...prev,
      page: 1,
      is_reload: true,
    }));
  }, [isMobile]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    const isBottom = scrollTop + clientHeight >= scrollHeight - 50;

    if (isBottom && hasMore && !loadingPage) {
      setLoadingPage(true);

      setParams((prev) => ({
        ...prev,
        page: prev.page + 1,
        is_reload: false,
      }));
    }
  };

  const handleOpen = () => {
    setShowOffcanvas(true);
  };

  const handleClose = () => {
    setShowOffcanvas(false);
    setParams({
      limit: 10,
      page: 1,
      search: "",
      is_reload: true,
    });
    setFilters({
      search: "",
    });
  };

  return (
    <>
      <div className="h-full flex flex-col animate-fade-up space-y-4 md:space-y-6">
        <div className="flex justify-between items-center px-2">
          <div>
            <h6 className="text-xl md:text-2xl font-black text-slate-800">
              {t("store")}
            </h6>

            <p className="text-[10px] md:text-xs text-gray-400 font-black uppercase tracking-widest">
              {totalRecord} {t("storeOnboarded")}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button className="mobile-filter-btn hidden" onClick={handleOpen}>
              <Search size={18} />
            </button>

            <button className="desktop-filter-btn hidden" onClick={handleOpen}>
              <Search size={18} />
              <span>{t("filter")}</span>
            </button>

            <div className="hidden md:flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
              {" "}
              <button
                onClick={() => setViewMode("grid")}
                disabled={viewMode === "grid"}
                title={t("switchToGridView")}
                className={`p-2 transition flex items-center justify-center 
                  ${
                    viewMode === "grid"
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                      : "bg-white text-slate-600 hover:bg-orange-100"
                  }`}
              >
                <Grid size={16} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                disabled={viewMode === "list"}
                title={t("switchToListView")}
                className={`
                p-2 transition flex items-center justify-center
                ${
                  viewMode === "list"
                    ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                    : "bg-white text-slate-600 hover:bg-orange-100"
                }`}
              >
                <List size={16} />
              </button>
            </div>

            {Number(auth.user.role_id) === CONFIG.ROLE_OWNER && (
              <button
                className="add-btn hidden"
                onClick={() => navigate("/add-store")}
              >
                <PlusLg />
                <span className="hidden md:block">{t("addNew")}</span>
              </button>
            )}
          </div>
        </div>
        {viewMode === "grid" ? (
          <div
            className="flex-1 space-y-3 md:space-y-4 overflow-y-auto pb-4 px-2 md:px-6 scroll-smooth"
            onScroll={handleScroll}
            style={{ marginBottom: "88px" }}
          >
            {skeletonLoading ? (
              <FlexibleSkeleton count={params.limit} />
            ) : storeList?.length > 0 ? (
              /* Grid layout: 1 column on mobile, 2 on tablet, 3 on desktop */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {storeList.map((item) => (
                  <div
                    key={item.uuid}
                    className="group bg-white dark:bg-[#0A1221] border border-slate-100 dark:border-white/[0.05] rounded-[24px] p-5 shadow-sm hover:shadow-md md:hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1 pr-2 min-w-0">
                          <h4 className="font-black text-slate-800 dark:text-white text-base md:text-lg leading-tight truncate">
                            {item.name}
                          </h4>

                          <div className="flex items-center gap-1.5 mt-1">
                            <div
                              className={`w-1.5 h-1.5 rounded-full ${
                                item.online_order == 1
                                  ? "bg-green-500"
                                  : "bg-orange-500"
                              }`}
                            ></div>

                            <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em]">
                              {item.business_type}
                            </span>
                          </div>
                        </div>

                        <span
                          className={`text-[9px] px-2.5 py-1 rounded-lg font-black uppercase tracking-widest border transition-colors ${
                            item.status === 1
                              ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20"
                              : "bg-red-500/10 text-red-500 border-red-500/20"
                          }`}
                        >
                          {item.status === 1 ? "Active" : "Inactive"}
                        </span>
                      </div>

                      {/* Data Rows: Tightened Spacing */}
                      <div className="space-y-2.5 border-t border-slate-50 dark:border-white/[0.03] pt-4">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 font-bold text-[10px] uppercase tracking-tight">
                            {t("mobile")}
                          </span>
                          <span className="text-slate-500 dark:text-slate-300 font-black text-sm">
                            {item.mobile}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 font-bold text-[10px] uppercase tracking-tight">
                            {t("ownerName")}
                          </span>
                          <span className="text-slate-500 dark:text-slate-300 font-black text-sm">
                            {item.owner_name}
                          </span>
                        </div>
                        <div className="flex items-start gap-2.5 group">
                          <div className="shrink-0 mt-0.5">
                            <GeoAlt
                              size={14}
                              className="text-slate-400 group-hover:text-orange-500 transition-colors cursor-pointer"
                            />
                          </div>

                          <p className="text-slate-600 dark:text-slate-300 font-bold text-[13px] leading-relaxed break-words">
                            {item.store_address}
                          </p>
                        </div>
                      </div>
                    </div>

                    {Number(auth.user.role_id) === CONFIG.ROLE_OWNER && (
                      <div className="grid grid-cols-1 gap-2 mt-6">
                        <button
                          onClick={() => navigate(`/update-store/${item.uuid}`)}
                          className="flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-slate-100 dark:border-slate-800 text-slate-600 dark:text-slate-400 font-black text-xs uppercase tracking-[0.15em] hover:bg-slate-50 dark:hover:bg-slate-800 transition-all active:scale-[0.98] w-full"
                        >
                          <PencilSquare size={16} strokeWidth={2.5} />
                          {t("edit")}
                        </button>
                        <button
                          onClick={(e) => handleDeleteShow(e, item)}
                          className="hidden flex items-center justify-center gap-2 py-2.5 rounded-xl bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 font-black text-[11px] uppercase tracking-widest hover:bg-red-100 dark:hover:bg-red-500/20 transition-all active:scale-95"
                        >
                          <Trash3 size={14} strokeWidth={2.5} /> {t("delete")}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              /* Empty State: Centered & Clean */
              <div className="flex flex-col items-center justify-center py-20 text-center max-w-sm mx-auto">
                <div className="w-20 h-20 bg-slate-50 dark:bg-slate-900 rounded-full flex items-center justify-center mb-6">
                  <BoxSeam size={32} className="text-slate-300" />
                </div>
                <h3 className="text-xl font-black text-slate-800 dark:text-white leading-tight">
                  {t("noStoresFound")}
                </h3>
                <p className="text-sm font-bold text-slate-500 mt-2 mb-8">
                  {t("tryAdjustingFilter")}
                </p>
                <button
                  onClick={handleReset}
                  className="w-full py-4 bg-[#E67E22] text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-orange-500/20 active:scale-95 transition-all"
                >
                  {t("resetFilter")}
                </button>
              </div>
            )}

            {loadingPage && (
              <div className="flex justify-center py-6">
                <div className="w-6 h-6 border-2 border-slate-200 border-t-[#E67E22] rounded-full animate-spin"></div>
              </div>
            )}
          </div>
        ) : (
          <div className="hidden md:flex bg-white dark:bg-slate-900 rounded-[40px] border border-slate-100 dark:border-slate-800 shadow-sm flex-1 flex-col overflow-hidden transition-colors min-h-0">
            <div className="overflow-auto flex-1 custom-scrollbar">
              <table className="w-full text-left border-separate border-spacing-0">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-text first:rounded-tl-[40px]">
                      #
                    </th>
                    <th className="table-header-text">{t("name")}</th>
                    <th className="table-header-text">{t("ownerName")}</th>
                    <th className="table-header-text">{t("mobile")}</th>
                    <th className="table-header-text">{t("status")}</th>
                    <th className="table-header-text">{t("businessType")}</th>
                    {Number(auth.user.role_id) === CONFIG.ROLE_OWNER && (
                      <th className="table-header-text text-right">
                        {t("action")}
                      </th>
                    )}
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                  {loading ? (
                    <DesktopTableSkeleton
                      rows={storeList?.length || 5}
                      columns={7}
                    />
                  ) : storeList?.length > 0 ? (
                    storeList?.map((item, i) => (
                      <tr key={i} className="border-b">
                        <td className="table-header-body font-bold">
                          {item.index}
                        </td>
                        <td className="table-header-body">{item.name}</td>
                        <td className="table-header-body">{item.owner_name}</td>
                        <td className="table-header-body">{item.mobile}</td>
                        <td className="table-header-body">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              item.status === 1
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {item.status === 1 ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="table-header-body">
                          {item.business_type}
                        </td>
                        {Number(auth.user.role_id) === CONFIG.ROLE_OWNER && (
                          <td className="table-header-body">
                            <div className="flex items-center justify-end gap-2">
                              <TooltipWrapper title="Edit" placement="top">
                                <button
                                  className="edit-btn"
                                  onClick={() =>
                                    navigate(`/update-store/${item.uuid}`)
                                  }
                                >
                                  <PencilSquare />
                                </button>
                              </TooltipWrapper>
                              <TooltipWrapper title="Delete" placement="top">
                                <button
                                  className="hidden delete-btn"
                                  onClick={(e) => handleDeleteShow(e, item)}
                                >
                                  <Trash3 />
                                </button>
                              </TooltipWrapper>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center py-6 text-slate-400 dark:text-slate-500 font-medium"
                      >
                        {t("noStoresFound")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

            
            </div>
              {storeList?.length > 0 && (
                <div className="p-4 md:p-6 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex flex-col md:flex-row justify-between items-center rounded-b-[40px] transition-colors hidden md:flex">
                  <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mb-4 md:mb-0">
                    {t("showing")}{" "}
                    <span className="font-bold text-slate-700 dark:text-slate-200">
                      {startIndex + 1}
                    </span>{" "}
                    {t("to")}{" "}
                    <span className="font-bold text-slate-700 dark:text-slate-200">
                      {Math.min(endIndex, totalRecord)}
                    </span>{" "}
                    {t("of")} {totalRecord}
                  </p>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleParams(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 transition-all"
                    >
                      <ChevronLeft size={18} />
                    </button>

                    <div className="hidden md:flex gap-2">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <button
                            key={page}
                            onClick={() => handleParams(page)}
                            className={`w-9 h-9 rounded-xl text-sm font-black transition-all ${
                              currentPage === page
                                ? "bg-brand-orange text-white shadow-lg shadow-orange-200"
                                : "text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
                            }`}
                          >
                            {page}
                          </button>
                        ),
                      )}
                    </div>

                    <div className="md:hidden px-4">
                      <span className="text-sm font-black text-brand-orange">
                        {t("page")} {currentPage} {t("of")} {totalPages}
                      </span>
                    </div>

                    <button
                      onClick={() => handleParams(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 transition-all"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}
          </div>
        )}
      </div>

      <Drawer
        isOpen={showOffcanvas}
        title={t("filterStore")}
        onClose={() => setShowOffcanvas(false)}
        footer={
          <>
            <div className="flex gap-3 flex-row justify-end w-full">
              <button className="btn-filter-close" onClick={handleReset}>
                {t("reset")}
              </button>

              <button className="btn-submit-orange" onClick={handleSearch}>
                {t("applyFilters")}
              </button>
            </div>
          </>
        }
      >
        <div className="space-y-4 p-2">
          <div className="flex flex-col gap-2">
            <label htmlFor="search" className="input-label">
              {t("search")}
            </label>
            <input
              type="text"
              name="search"
              className="input-field"
              value={filters?.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
            />
          </div>
        </div>
      </Drawer>

      <ConfirmModal
        open={deleteShow}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteStore}
        loading={btnDeleteLoading}
        titleKey={t("deleteStore")}
        descriptionKey={t("deleteConfirmation", { name: deleteId?.name })}
        confirmText="Yes, Delete"
        icon={<Trash3 size={32} className="text-red-500" />}
      />
    </>
  );
};

export default Store;
